import { GROESSE_LABELS, TASK_EVENT_TYPE_LABELS, TASK_STATUS_LABELS } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { aktivitaetQuery } from '../lib/queries'
import { AufgabenVerweis } from './AufgabeKontext'
import { TagesPopup } from './TagesPopup'
import { Button } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * **Aktivitätsbericht einer Person** (18.08.2026, [F-45](../../../../docs/OFFENE-FRAGEN.md)).
 *
 * Nils: *„Ein Aktivitätsbericht im Detail, dass ich beurteilen kann, wie jemand arbeitet."*
 * Und auf die Rückfrage: *„ok, dann ohne Bewertung, die muss ich dann selber machen."*
 *
 * ## Was hier bewusst fehlt
 *
 * Keine Effektivität, kein Index, kein Vergleich mit anderen. Der Bericht zeigt **Tätigkeit
 * und Verfügbarkeit**; die Beurteilung macht ein Mensch, der den Kontext kennt
 * ([E-111](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Zwei Stellen, an denen das konkret wird:
 *
 * - Die Größen stehen als **Aufschlüsselung** („4 klein · 9 mittel · 2 groß"), nie als
 *   gewichtete Summe. Eine Summe bräuchte Gewichte, und die wären erfunden.
 * - Die Spalte heißt **Liegedauer**, nicht Bearbeitungszeit. Work weiß, wann jemand gezogen
 *   und wann er erledigt hat – was dazwischen war, weiß es nicht.
 */
export function Aktivitaetsbericht({
  personId,
  schliessen,
}: {
  personId: string
  schliessen: () => void
}) {
  const [tage, setTage] = useState(90)
  /**
   * **Der Tag im Popup** (Nils, 19.08.2026: *„Ein Klick auf den Tag sollte dann das Popup
   * öffnen mit den Infos zu dem Tag."*).
   *
   * Dasselbe `TagesPopup` wie im Leitstand, nicht ein zweites – es beantwortet genau diese
   * Frage schon seit Phase 2 („was ist an diesem Tag passiert"), mit Filtern und
   * Tastaturbedienung. Ein eigenes hier wäre die zweite Stelle, an der derselbe Tag anders
   * aussieht.
   */
  const [tagOffen, setTagOffen] = useState<string | null>(null)
  const { data, isLoading, error } = useQuery(aktivitaetQuery(personId, tage))

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[6vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Aktivitätsbericht"
    >
      <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(88vh,100%)] w-full min-w-0 max-w-[900px] flex-col rounded-card border border-line bg-surface shadow-card">
        <div className="flex items-center gap-2 border-b border-line px-[18px] py-3.5">
          <h3 className="text-sm font-bold">
            {isLoading ? 'Lädt …' : (data?.person.name ?? 'Unbekannt')}
          </h3>
          <span className="text-[11.5px] text-muted">Aktivität</span>
          <div className="ml-auto flex items-center gap-1.5">
            {[30, 90, 365].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTage(t)}
                className={`cursor-pointer rounded-lg px-2 py-[3px] text-[11.5px] font-semibold ${
                  t === tage ? 'bg-ink text-white' : 'border border-line2 text-ink2'
                }`}
              >
                {t === 365 ? '1 Jahr' : `${t} Tage`}
              </button>
            ))}
            <button
              type="button"
              onClick={schliessen}
              className="ml-1 cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
              aria-label="Schließen"
            >
              ✕
            </button>
          </div>
        </div>

        {error && (
          <p
            role="alert"
            className="m-[18px] rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]"
          >
            {error.message}
          </p>
        )}

        {data && (
          <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3.5">
            {/**
             * **Die Zusammenfassung ist eine Aufzählung, keine Note.** Wer sie liest, sieht
             * Mengen und Größen – und zieht seinen Schluss selbst.
             */}
            <div className="flex flex-wrap gap-4 rounded-lg bg-[#f7f6f4] px-3 py-2.5 text-[12.5px]">
              <span>
                <strong className="text-[16px]">{data.erledigt.length}</strong> erledigt
              </span>
              <span className="text-ink2">
                {data.groessen.klein} klein · {data.groessen.mittel} mittel · {data.groessen.gross}{' '}
                groß
              </span>
              <span className="text-ink2">
                <strong>{data.offen.length}</strong> offen auf dem Tisch
              </span>
              {data.abwesenheiten.length > 0 && (
                <span className="text-ink2">
                  {data.abwesenheiten.length} Abwesenheit
                  {data.abwesenheiten.length === 1 ? '' : 'en'}
                </span>
              )}
            </div>

            <p className="mt-1.5 text-[11px] text-muted">
              Ohne Wertung, so gewollt. Die Größen stehen einzeln und werden nicht verrechnet –
              Gewichte dafür wären erfunden. „Liegedauer" ist die Zeit von „an sich genommen" bis
              „erledigt", <strong>nicht</strong> die Arbeitszeit.
            </p>

            {/**
             * **Der Kalender** (Nils, 18.08.2026: *„dass ich sehe, an welchen Tagen
             * gearbeitet wurde, wann krank/Urlaub, wann ggf. gar nichts passiert ist, obwohl
             * anwesend war."*).
             *
             * Drei Lagen, drei Farben – und die dritte ist die, um die es geht: **anwesend,
             * aber keine Spur.** Sie entsteht nicht aus einer Zahl, sondern aus dem Fehlen
             * einer, und deshalb ist der Kalender lückenlos statt nur die Tage zu zeigen,
             * an denen etwas war.
             *
             * Wochenenden sind blass: „nichts passiert" ist dort keine Auffälligkeit.
             */}
            <h4 className="mt-4 mb-1.5 text-xs font-bold">Kalender</h4>
            <Kalender tage={data.kalender} beiTagKlick={setTagOffen} />

            {data.offen.length > 0 && (
              <>
                <h4 className="mt-4 mb-1.5 text-xs font-bold">Liegt gerade dort</h4>
                {data.offen
                  .slice()
                  .sort((a, b) => b.seitTagen - a.seitTagen)
                  .map((o) => (
                    <div
                      key={o.taskId}
                      className="flex flex-wrap items-center gap-2 border-b border-line py-1 text-[12.5px] last:border-b-0"
                    >
                      <span
                        className={`tabular w-[58px] shrink-0 ${
                          o.seitTagen > 60 ? 'font-semibold text-pool-sofort' : 'text-muted'
                        }`}
                      >
                        {o.seitTagen} T
                      </span>
                      <span className="w-[76px] shrink-0 text-[11px] text-muted">
                        {TASK_STATUS_LABELS[o.status as keyof typeof TASK_STATUS_LABELS] ??
                          o.status}
                      </span>
                      <span className="w-[46px] shrink-0 text-[11px] text-muted">
                        {GROESSE_LABELS[o.groesse as keyof typeof GROESSE_LABELS]}
                      </span>
                      <AufgabenVerweis
                        aufgabeId={o.taskId}
                        className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
                        title={o.titel}
                      >
                        {o.titel}
                      </AufgabenVerweis>
                      {/**
                       * **„mit" – die Aufgabe steht hier, gehört aber jemand anderem**
                       * (28.08.2026, [E-266](../../../../docs/ENTSCHEIDUNGEN.md#e-266)).
                       *
                       * Nils wollte Aufgaben mit mehreren GitLab-Bearbeitern bei allen sehen.
                       * Sie **zählen** weiter beim Führenden – sonst wäre die Summe der
                       * Auslastung größer als der Bestand. Ohne diese Marke widerspräche die
                       * Liste der Zahl darüber: fünf Zeilen unter „3 in Arbeit".
                       */}
                      {o.mit && (
                        <span
                          className="shrink-0 rounded-md bg-[#f2f1ed] px-1.5 py-px text-[10.5px] text-muted"
                          title="Mit-Bearbeiter aus der Quelle – gezählt wird die Aufgabe beim führenden Bearbeiter"
                        >
                          mit
                        </span>
                      )}
                    </div>
                  ))}
              </>
            )}

            {data.erledigt.length > 0 && (
              <>
                <h4 className="mt-4 mb-1.5 text-xs font-bold">Erledigt</h4>
                {data.erledigt.slice(0, 60).map((e) => (
                  <div
                    key={`${e.taskId}${String(e.am)}`}
                    className="flex flex-wrap items-center gap-2 border-b border-line py-1 text-[12.5px] last:border-b-0"
                  >
                    <span className="tabular w-[62px] shrink-0 text-muted">
                      {new Date(e.am).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                      })}
                    </span>
                    <span className="w-[46px] shrink-0 text-[11px] text-muted">
                      {GROESSE_LABELS[e.groesse as keyof typeof GROESSE_LABELS]}
                    </span>
                    <AufgabenVerweis
                      aufgabeId={e.taskId}
                      className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
                      title={e.titel}
                    >
                      {e.kennung ? `${e.kennung} · ` : ''}
                      {e.titel}
                    </AufgabenVerweis>
                    <span className="w-[92px] shrink-0 text-right text-[11px] text-muted">
                      {e.liegedauerTage === null ? '–' : `lag ${e.liegedauerTage} T`}
                    </span>
                  </div>
                ))}
              </>
            )}

            {data.abwesenheiten.length > 0 && (
              <>
                <h4 className="mt-4 mb-1.5 text-xs font-bold">Abwesend</h4>
                {data.abwesenheiten.map((a) => (
                  <div
                    key={`${a.von}${a.bis}`}
                    className="flex flex-wrap items-center gap-2 border-b border-line py-1 text-[12.5px] last:border-b-0"
                  >
                    <span className="tabular w-[150px] shrink-0">
                      {new Date(a.von).toLocaleDateString('de-DE')} –{' '}
                      {new Date(a.bis).toLocaleDateString('de-DE')}
                    </span>
                    <span className="text-ink2">{a.werktage ? `${a.werktage} Werktage` : ''}</span>
                    <span className="ml-auto text-[11px] text-muted">{a.art ?? ''}</span>
                  </div>
                ))}
                {/**
                 * **Die Quelle kennt keine Art.** Nils fragte nach „Urlaub oder krank" – das
                 * Intranet liefert nur den Genehmigungszustand. Das dranzuschreiben ist
                 * ehrlicher, als „Urlaub" zu raten.
                 */}
                <p className="mt-1 text-[11px] text-muted">
                  Das Intranet unterscheidet nicht zwischen Urlaub und Krankheit – rechts steht der
                  Genehmigungszustand, nicht die Art.
                </p>
              </>
            )}

            <h4 className="mt-4 mb-1.5 text-xs font-bold">Verlauf</h4>
            {data.verlauf.length === 0 ? (
              <p className="text-[12.5px] text-ink2">
                In diesem Zeitraum ist von dieser Person nichts im Log.
              </p>
            ) : (
              data.verlauf.slice(0, 80).map((v, i) => (
                <div
                  key={`${v.taskId}${String(v.am)}${i}`}
                  className="flex flex-wrap items-center gap-2 border-b border-line py-1 text-[12px] last:border-b-0"
                >
                  <span className="tabular w-[92px] shrink-0 text-muted">
                    {new Date(v.am).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                    })}{' '}
                    {new Date(v.am).toLocaleTimeString('de-DE', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span className="w-[120px] shrink-0 text-[11px] text-ink2">
                    {TASK_EVENT_TYPE_LABELS[v.typ] ?? v.typ}
                    {v.nach
                      ? ` → ${TASK_STATUS_LABELS[v.nach as keyof typeof TASK_STATUS_LABELS] ?? v.nach}`
                      : ''}
                  </span>
                  <AufgabenVerweis
                    aufgabeId={v.taskId}
                    className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
                    title={v.titel}
                  >
                    {v.titel}
                  </AufgabenVerweis>
                </div>
              ))
            )}
          </div>
        )}

        <div className="border-t border-line px-[18px] py-2.5">
          <Button onClick={schliessen}>Schließen</Button>
        </div>
      </div>

      {/**
       * **Das Tages-Popup liegt über dem Bericht**, nicht statt seiner: wer einen Tag
       * ansieht, will danach den nächsten anklicken. Es schließt allein, der Bericht bleibt
       * offen.
       */}
      {tagOffen && data && (
        <TagesPopup
          auswahl={{ personId: data.person.id, name: data.person.name, tag: tagOffen }}
          schliessen={() => setTagOffen(null)}
        />
      )}
    </div>
  )
}

/**
 * **Der Kalender als Kalender** (18.08.2026, zweiter Anlauf).
 *
 * Nils zum ersten: *„Da mit dem Kalender, da kann ich nichts richtig erkennen oder deuten."*
 * Er hatte recht. Es waren zwei fortlaufende Reihen aus 90 Kästchen – ohne Wochentag, ohne
 * Datum, ohne Monat. Man sah, **dass** Farben verschieden sind, aber nicht **wann**, und
 * damit war das Bild wertlos: die Frage lautet ja gerade „an welchen Tagen".
 *
 * ## Was diese Fassung anders macht
 *
 * **Eine Spalte je Woche, sieben Zeilen für Mo–So** – dieselbe Anordnung wie in jedem
 * Kalender, und der Wochenrhythmus wird zur Struktur statt zum Zufall. Darüber die
 * Monatsnamen, links die Wochentage. Wer eine Lücke sieht, weiß sofort, ob sie am
 * Wochenende liegt.
 *
 * **Wochenenden sind nur ein feiner Umriss**, kein gefülltes Kästchen. Vorher waren sie so
 * kräftig wie ein Arbeitstag und machten ein Drittel des Bildes aus – Rauschen, das die
 * Frage überdeckt.
 *
 * **Die Zahl der Ereignisse steuert die Sättigung** in vier Stufen. Eine einzige Farbe für
 * „gearbeitet" beantwortet nicht, ob es ein Klick war oder ein Tag Arbeit.
 */
function Kalender({
  tage,
  beiTagKlick,
}: {
  tage: Aktivitaetstag[]
  beiTagKlick: (tag: string) => void
}) {
  /**
   * **„Nur Erledigt"** (Nils, 19.08.2026: *„dann sieht man noch besser den Erfolg."*).
   *
   * Dieselben Tage, andere Zahl: statt aller Ereignisse färbt die Zahl der **Erledigungen**.
   * Beide Sichten haben ihren Zweck – „alles" beantwortet *war jemand tätig*, „nur erledigt"
   * *kam etwas dabei heraus*.
   *
   * Die Zahl liegt seit dem ersten Bau in den Daten (`kalender[].erledigt`); es ist ein
   * Umschalter, keine neue Abfrage.
   */
  const [nurErledigt, setNurErledigt] = useState(false)

  if (tage.length === 0) return null

  /**
   * **Aufgefüllt bis zum Montag.** Ohne die Lücken vorn stünde der erste Tag in der Zeile
   * seines Wochentags, aber die Spalte wäre um so viele Tage verschoben – und die ganze
   * Anordnung wäre eine Zufallsfolge statt eines Kalenders.
   */
  const ersterTag = new Date(`${tage[0]!.tag}T12:00:00Z`)
  const vorlauf = (ersterTag.getUTCDay() + 6) % 7
  const zellen: (Aktivitaetstag | null)[] = [...Array<null>(vorlauf).fill(null), ...tage]

  const wochen: (Aktivitaetstag | null)[][] = []
  for (let i = 0; i < zellen.length; i += 7) wochen.push(zellen.slice(i, i + 7))

  const MONATE = [
    'Jan',
    'Feb',
    'Mär',
    'Apr',
    'Mai',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Okt',
    'Nov',
    'Dez',
  ]

  return (
    <>
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
        {(
          [
            [false, 'alles im Log'],
            [true, 'nur Erledigt'],
          ] as const
        ).map(([wert, beschriftung]) => (
          <button
            key={beschriftung}
            type="button"
            onClick={() => setNurErledigt(wert)}
            className={`cursor-pointer rounded-lg px-2 py-[2px] font-semibold ${
              nurErledigt === wert ? 'bg-ink text-white' : 'border border-line2 text-ink2'
            }`}
          >
            {beschriftung}
          </button>
        ))}
        <span className="text-muted">
          {nurErledigt
            ? 'Gefärbt nach abgeschlossenen Aufgaben – tätige Tage ohne Abschluss bleiben blass'
            : 'Gefärbt nach allen Ereignissen dieser Person'}
        </span>
      </div>

      <div className="flex gap-[3px] overflow-x-auto pb-1">
        <div className="flex shrink-0 flex-col gap-[3px] pt-[14px] text-[9px] text-muted">
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((t) => (
            <span key={t} className="flex h-[13px] items-center">
              {t}
            </span>
          ))}
        </div>

        {wochen.map((woche, wi) => {
          /** Der Monatsname steht über der Woche, in der er anfängt. */
          const ersterEchte = woche.find((t): t is Aktivitaetstag => t !== null)
          const vorigeWoche = wochen[wi - 1]?.find((t): t is Aktivitaetstag => t !== null)
          const monat = ersterEchte ? new Date(`${ersterEchte.tag}T12:00:00Z`).getUTCMonth() : null
          const vorigerMonat = vorigeWoche
            ? new Date(`${vorigeWoche.tag}T12:00:00Z`).getUTCMonth()
            : null

          return (
            <div key={wi} className="flex shrink-0 flex-col gap-[3px]">
              <span className="h-[12px] text-[9px] text-muted">
                {monat !== null && monat !== vorigerMonat ? MONATE[monat] : ''}
              </span>
              {Array.from({ length: 7 }, (_, zi) => {
                const t = woche[zi] ?? null
                if (!t) return <span key={zi} className="h-[13px] w-[13px]" />
                return (
                  <Tageskaestchen
                    key={t.tag}
                    tag={t}
                    nurErledigt={nurErledigt}
                    beiKlick={beiTagKlick}
                  />
                )
              })}
            </div>
          )
        })}
      </div>

      <div className="mt-1.5 flex flex-wrap gap-3 text-[10.5px] text-muted">
        <span className="flex items-center gap-1">
          <i className="h-[10px] w-[10px] rounded-[2px] bg-accent/30" />
          <i className="h-[10px] w-[10px] rounded-[2px] bg-accent/60" />
          <i className="h-[10px] w-[10px] rounded-[2px] bg-accent" />{' '}
          {nurErledigt ? 'wenig → viel erledigt' : 'wenig → viel im Log'}
        </span>
        <span className="flex items-center gap-1">
          <i className="h-[10px] w-[10px] rounded-[2px] border border-[#9aa7b8] bg-[repeating-linear-gradient(45deg,#c3ccd9_0,#c3ccd9_2px,#eef1f5_2px,#eef1f5_4px)]" />
          abwesend
        </span>
        {nurErledigt && (
          <span className="flex items-center gap-1">
            <i className="h-[10px] w-[10px] rounded-[2px] border border-line2 bg-[#f3f2ef]" />
            tätig, nichts abgeschlossen
          </span>
        )}
        <span className="flex items-center gap-1">
          <i className="h-[10px] w-[10px] rounded-[2px] border border-[#e8b4b4] bg-[#fdeaea]" />
          anwesend, nichts im Log
        </span>
        <span className="flex items-center gap-1">
          <i className="h-[10px] w-[10px] rounded-[2px] border border-line2" /> Wochenende
        </span>
      </div>
    </>
  )
}

type Aktivitaetstag = {
  tag: string
  ereignisse: number
  erledigt: number
  abwesend: boolean
  wochenende: boolean
}

function Tageskaestchen({
  tag,
  nurErledigt,
  beiKlick,
}: {
  tag: Aktivitaetstag
  nurErledigt: boolean
  beiKlick: (tag: string) => void
}) {
  /**
   * **Was die Farbe zählt** – und was ein leerer Tag dann bedeutet.
   *
   * Der rote Kasten heißt *„anwesend, aber keine Spur"*. Er hängt deshalb **immer an den
   * Ereignissen**, auch in der Sicht „nur Erledigt": wer einen Tag lang kommentiert und
   * zugewiesen, aber nichts abgeschlossen hat, hat gearbeitet. Ihn dafür rot zu färben wäre
   * die Unterstellung, die dieser Bericht ausdrücklich nicht machen soll
   * ([E-111](../../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Ein solcher Tag ist in dieser Sicht **blass, nicht rot** – tätig, aber ohne Abschluss.
   */
  const menge = nurErledigt ? tag.erledigt : tag.ereignisse
  const taetig = tag.ereignisse > 0
  /**
   * **Abwesenheit ist gestreift, nicht blau** (Nils, 19.08.2026: *„Die Farben von abwesend
   * unterscheiden sich jedoch nicht wirklich von viel→wenig im Log."*).
   *
   * Er hat recht, und der Fehler war die Farbwahl selbst: „abwesend" war ein helles Blau –
   * also **dieselbe Farbfamilie** wie die drei Sättigungsstufen für Arbeit. Nebeneinander
   * ist ein hellblaues Kästchen dann nicht von „wenig gearbeitet" zu unterscheiden, und
   * genau diese beiden dürfen nie verwechselt werden: das eine heißt „war nicht da", das
   * andere „war da und hat wenig getan".
   *
   * Ein **Muster** trennt schärfer als jede Farbe, weil es keine Stufe einer Skala sein
   * kann. Die Schraffur sagt „kein Arbeitstag" auf einen Blick – auch bei
   * Rot-Grün-Schwäche und auch im Ausdruck.
   */
  const stil = tag.abwesend
    ? 'border border-[#9aa7b8] bg-[repeating-linear-gradient(45deg,#c3ccd9_0,#c3ccd9_2px,#eef1f5_2px,#eef1f5_4px)]'
    : menge === 0
      ? tag.wochenende
        ? 'border border-line2'
        : taetig
          ? /** Tätig, aber nichts abgeschlossen – blass, nicht rot. */
            'border border-line2 bg-[#f3f2ef]'
          : 'border border-[#e8b4b4] bg-[#fdeaea]'
      : menge >= (nurErledigt ? 3 : 8)
        ? 'bg-accent'
        : menge >= (nurErledigt ? 2 : 3)
          ? 'bg-accent/60'
          : 'bg-accent/30'

  const lage = tag.abwesend
    ? 'abwesend'
    : tag.wochenende && tag.ereignisse === 0
      ? 'Wochenende'
      : nurErledigt
        ? tag.erledigt > 0
          ? `${tag.erledigt} erledigt`
          : taetig
            ? `nichts abgeschlossen (${tag.ereignisse} Ereignis${tag.ereignisse === 1 ? '' : 'se'})`
            : 'anwesend, nichts im Log'
        : tag.ereignisse === 0
          ? 'anwesend, nichts im Log'
          : `${tag.ereignisse} Ereignis${tag.ereignisse === 1 ? '' : 'se'}${
              tag.erledigt > 0 ? `, ${tag.erledigt} erledigt` : ''
            }`

  /**
   * **Ein Tag ohne Ereignisse ist nicht anklickbar** – das Popup hätte nichts zu zeigen, und
   * ein Klick, der nichts tut, liest sich wie ein Fehler. Der Mauszeiger sagt es vorher.
   */
  const anklickbar = tag.ereignisse > 0

  return (
    <button
      type="button"
      disabled={!anklickbar}
      onClick={() => beiKlick(tag.tag)}
      className={`h-[13px] w-[13px] rounded-[3px] ${stil} ${
        anklickbar ? 'cursor-pointer hover:ring-2 hover:ring-ink/30' : 'cursor-default'
      }`}
      title={`${new Date(tag.tag).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
      })} – ${lage}${anklickbar ? ' · klicken zeigt den Tag' : ''}`}
      /**
       * **Ausgeschriebenes Datum, feste Stellenzahl.** `toLocaleDateString('de-DE')` liefert
       * ohne Optionen „12.8.2026" – je nach Umgebung mit oder ohne führende Null. Für einen
       * zugänglichen Namen, den ein Screenreader vorliest und ein Test sucht, ist das die
       * falsche Sorte Freiheit.
       */
      aria-label={`${new Date(tag.tag).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })}: ${lage}`}
    />
  )
}
